/**
 * アプリ（Capacitor の殻）の中で動いているかどうかと、そこでの「外向きの住所」。
 *
 * アプリの中では画面が `capacitor://localhost`（iOS）や `https://localhost`（Android）から配られる。
 * そのため、そのまま `location.origin` を使うと次の2つが壊れる。
 * - 共有するURL（友だちが開けない `capacitor://localhost/fukuoka` になる）
 * - ランキングAPI（`/api/daily` がアプリの中を探しに行き、見つからない）
 *
 * ここでは @capacitor/core を import しない。殻に入ったときだけ `window.Capacitor` が生えるので、
 * それを見る。Web版のバンドルにアプリ用のコードを混ぜないため。
 */

export type NativeOS = 'ios' | 'android';

/** 本番の Web 版。共有URLとランキングAPIはここへ向ける。 */
export const PUBLIC_ORIGIN = 'https://gotochi-meshi-merge.pages.dev';

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

function capacitor(): CapacitorGlobal | undefined {
  return (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor;
}

export function isNativeApp(): boolean {
  return Boolean(capacitor()?.isNativePlatform?.());
}

/** アプリの中なら 'ios' か 'android'。Web なら null。 */
export function nativeOS(): NativeOS | null {
  if (!isNativeApp()) return null;
  const p = capacitor()?.getPlatform?.();
  return p === 'ios' || p === 'android' ? p : null;
}

/**
 * ランキングAPIの向き先。アプリのビルドで `VITE_API_ORIGIN` を渡すと差し替えられる
 * （staging の Functions に向けて試すとき）。
 */
function apiOrigin(): string {
  const fromEnv = (import.meta.env?.VITE_API_ORIGIN as string | undefined)?.replace(/\/+$/, '');
  return fromEnv || PUBLIC_ORIGIN;
}

/** `/api/...` を、いま動いている場所で届くURLにする。Web ではそのまま（同じサイト）。 */
export function apiUrl(path: string): string {
  return isNativeApp() ? `${apiOrigin()}${path}` : path;
}

/** 共有に使う「外から開ける」オリジン。アプリの中では本番の Web 版。 */
export function publicOrigin(): string {
  return isNativeApp() ? PUBLIC_ORIGIN : location.origin;
}

/** 共有画像に書く「外から開ける」ホスト名。アプリの中では本番の Web 版（localhost を出さない）。 */
export function publicHost(): string {
  try {
    return new URL(publicOrigin()).host;
  } catch {
    return new URL(PUBLIC_ORIGIN).host;
  }
}
